import type { Account, Customer } from '../types/account'

/**
 * Twee dossiers van dezelfde klant samenvoegen (BEHEER-PLAN §3.3). Het dossier dat je open hebt
 * (`target`) blijft; het andere (`source`) gaat erin op en verdwijnt. Bestellingen, afspraken,
 * kassabonnen, tegoed en notities verhuizen naar `target` (dat doet de server per tabel); deze
 * functie bepaalt wat er van het klantdossier zelf overblijft, en wanneer het niet kan.
 * Puur en getest; de demo-server gebruikt hetzelfde.
 */

export type MergeProblemCode = 'same_customer' | 'two_logins' | 'two_subscriptions' | 'two_courses'
export type MergeProblem = { code: MergeProblemCode; message: string }

const filled = (s: string) => s.trim() !== ''
const activeSubscription = (a: Account) => Boolean(a.subscription && a.subscription.status !== 'gestopt')
const openCourse = (a: Account) => Boolean(a.course && a.course.done < a.course.total)
/** Een login = ze kan zelf inloggen (wachtwoord, of een bevestigd adres voor de inloglink). */
const hasLogin = (c: Customer) => filled(c.email) && (c.hasPassword || c.emailVerified)

export function mergeProblem(target: Account, source: Account): MergeProblem | null {
  const t = target.customer
  const s = source.customer
  if (t.id === s.id) return { code: 'same_customer', message: 'Dit is hetzelfde dossier.' }
  if (hasLogin(t) && hasLogin(s) && t.email.trim().toLowerCase() !== s.email.trim().toLowerCase()) {
    return { code: 'two_logins', message: 'Beide dossiers hebben een eigen login met een ander e-mailadres. Vraag de klant welk adres ze houdt; de server voegt twee logins niet samen.' }
  }
  if (activeSubscription(target) && activeSubscription(source)) {
    return { code: 'two_subscriptions', message: 'Beide dossiers hebben een lopend abonnement. Stop er eerst één.' }
  }
  if (openCourse(target) && openCourse(source)) {
    return { code: 'two_courses', message: 'Beide dossiers hebben een lopende kuur. Rond er eerst één af of stop hem.' }
  }
  return null
}

/** Wat er van `target` en `source` één klant wordt. Gooit niet; roep eerst `mergeProblem` aan. */
export function mergeAccounts(target: Account, source: Account): Account {
  const t = target.customer
  const s = source.customer
  // Het e-mailadres van wie een login heeft wint; anders dat van het open dossier, anders het andere.
  const emailFrom = hasLogin(s) && !hasLogin(t) ? s : filled(t.email) ? t : s
  const customer: Customer = {
    ...t,
    firstName: filled(t.firstName) ? t.firstName : s.firstName,
    lastName: filled(t.lastName) ? t.lastName : s.lastName,
    email: emailFrom.email,
    emailVerified: emailFrom.emailVerified,
    hasPassword: emailFrom.hasPassword,
    phone: filled(t.phone) ? t.phone : s.phone,
    address: filled(t.address.street) ? t.address : s.address,
    // De vroegste datum: zo lang is ze al klant.
    memberSince: t.memberSince <= s.memberSince ? t.memberSince : s.memberSince,
    birthday: t.birthday ?? s.birthday ?? null,
    // De eerste herkomst wordt nooit overschreven (Customer.acquisitionSource).
    acquisitionSource: earliestSource(t.acquisitionSource, s.acquisitionSource),
    // Toestemming nooit verruimen: alleen wat het open dossier al had, of het andere als hier niets is.
    photoStorageConsent: t.photoStorageConsent ?? s.photoStorageConsent,
    diaryConsent: t.diaryConsent && s.diaryConsent ? true : t.diaryConsent && source.diary.length === 0,
    prefs: {
      refillWhatsApp: t.prefs.refillWhatsApp && s.prefs.refillWhatsApp,
      refillEmail: t.prefs.refillEmail && s.prefs.refillEmail,
      // Nieuwsbrief: alleen als het adres dat blijft er zelf voor koos.
      newsletter: emailFrom.prefs.newsletter,
    },
  }
  return {
    customer,
    plan: target.plan.items.length > 0 || source.plan.items.length === 0 ? target.plan : source.plan,
    subscription: activeSubscription(target) ? target.subscription : activeSubscription(source) ? source.subscription : (target.subscription ?? source.subscription),
    course: openCourse(target) ? target.course : openCourse(source) ? source.course : (target.course ?? source.course),
    visits: [...target.visits, ...source.visits].sort((a, b) => b.date.localeCompare(a.date)),
    diary: [...target.diary, ...source.diary].sort((a, b) => b.date.localeCompare(a.date)),
    appointments: [...target.appointments, ...source.appointments],
    orders: [...target.orders, ...source.orders],
  }
}

function earliestSource(a: Customer['acquisitionSource'], b: Customer['acquisitionSource']) {
  if (!a) return b ?? null
  if (!b) return a
  return a.at <= b.at ? a : b
}

/** Twee interne notities als één, met een scheidingslijn; lege vallen weg. */
export const mergeNotes = (target: string, source: string, sourceName: string) =>
  [target.trim(), source.trim() ? `Uit het samengevoegde dossier van ${sourceName}:\n${source.trim()}` : ''].filter(Boolean).join('\n\n')
